import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { apiFetch } from "../utils/api";
import Cargando from "../components/Cargando";
import { Ayuda } from "../context/AyudaContext";

const unidadVacia = (numero) => ({
  numero_unidad: numero,
  codigo_activo: "",
  marca: "",
  modelo: "",
  numero_serie: "",
  fecha_adquisicion: "",
  edificio_id: "",
  avisado: false,
  entregado_en: null,
  entregado_a: "",
});

// Solo se etiquetan las unidades que de verdad se van a comprar — si
// gerencia aprobó menos cantidad de la pedida, se llenan datos de esa
// cantidad aprobada, no de la cantidad original.
const cantidadRelevante = (item) =>
  item.cantidad_aprobada > 0 ? item.cantidad_aprobada : item.descartado ? 0 : item.cantidad;

const CAMPOS_DATOS = ["codigo_activo", "marca", "modelo", "numero_serie", "fecha_adquisicion", "edificio_id"];
const estaCompleta = (u) => CAMPOS_DATOS.every((c) => String(u[c] || "").trim() !== "");

// Pantalla dedicada para llenar el código/marca/modelo/serie/edificio de
// cada unidad de un producto marcado como activo fijo, en una tabla con una
// fila por unidad (sirve igual para 2 que para 50). Si se llegó desde "Avisar
// retiro", al terminar se vuelve a la solicitud y esa ventana se abre sola.
function DetalleActivoItem() {
  const { id, itemId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const desdeAvisar = !!location.state?.volverAAvisar;
  const usuario = JSON.parse(localStorage.getItem("usuario") || "null");
  const esCompras = usuario?.rol === "compras" || usuario?.rol === "administrador";

  const [solicitud, setSolicitud] = useState(null);
  const [edificios, setEdificios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [unidades, setUnidades] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [guardadoOk, setGuardadoOk] = useState(false);
  const [sinGuardar, setSinGuardar] = useState(false);
  const [filtro, setFiltro] = useState("");
  const [lote, setLote] = useState({ marca: "", modelo: "", fecha_adquisicion: "", edificio_id: "" });

  useEffect(() => {
    setCargando(true);
    setError("");
    Promise.all([apiFetch(`/api/solicitudes/${id}`), apiFetch("/api/edificios")])
      .then(([solicitudData, edificiosData]) => {
        setSolicitud(solicitudData);
        setEdificios(edificiosData || []);
        const item = (solicitudData.items || []).find((it) => String(it.id) === String(itemId));
        if (item) {
          const existentes = item.activos || [];
          setUnidades(
            Array.from({ length: cantidadRelevante(item) }, (_, i) => {
              const numero = i + 1;
              const existente = existentes.find((a) => a.numero_unidad === numero);
              return existente
                ? {
                    numero_unidad: numero,
                    codigo_activo: existente.codigo_activo || "",
                    marca: existente.marca || "",
                    modelo: existente.modelo || "",
                    numero_serie: existente.numero_serie || "",
                    fecha_adquisicion: existente.fecha_adquisicion ? existente.fecha_adquisicion.slice(0, 10) : "",
                    edificio_id: existente.edificio_id || "",
                    avisado: !!existente.avisado,
                    entregado_en: existente.entregado_en || null,
                    entregado_a: existente.entregado_a || "",
                  }
                : unidadVacia(numero);
            })
          );
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setCargando(false));
  }, [id, itemId]);

  const item = (solicitud?.items || []).find((it) => String(it.id) === String(itemId));

  // Códigos repetidos dentro de esta misma tabla — se marcan en rojo.
  const codigosRepetidos = useMemo(() => {
    const conteo = {};
    unidades.forEach((u) => {
      const c = String(u.codigo_activo || "").trim().toLowerCase();
      if (c) conteo[c] = (conteo[c] || 0) + 1;
    });
    return new Set(Object.keys(conteo).filter((c) => conteo[c] > 1));
  }, [unidades]);

  const actualizarUnidad = (numero, campo, valor) => {
    setUnidades((prev) => prev.map((u) => (u.numero_unidad === numero ? { ...u, [campo]: valor } : u)));
    setGuardadoOk(false);
    setSinGuardar(true);
  };

  // Rellena, en todas las unidades, SOLO los campos que estén vacíos con lo
  // escrito arriba (para no pisar lo que ya se llenó a mano).
  const aplicarALasVacias = () => {
    setUnidades((prev) =>
      prev.map((u) => {
        const nueva = { ...u };
        ["marca", "modelo", "fecha_adquisicion", "edificio_id"].forEach((c) => {
          if (lote[c] && !String(u[c] || "").trim()) nueva[c] = lote[c];
        });
        return nueva;
      })
    );
    setGuardadoOk(false);
    setSinGuardar(true);
  };

  const guardar = async () => {
    setGuardando(true);
    setError("");
    try {
      await apiFetch(`/api/items/${itemId}/activo`, {
        method: "POST",
        body: JSON.stringify({
          es_activo_fijo: true,
          activos: unidades.map((u) => ({
            numero_unidad: u.numero_unidad,
            codigo_activo: u.codigo_activo,
            marca: u.marca,
            modelo: u.modelo,
            numero_serie: u.numero_serie,
            fecha_adquisicion: u.fecha_adquisicion,
            edificio_id: u.edificio_id,
          })),
        }),
      });
      setGuardadoOk(true);
      setSinGuardar(false);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setGuardando(false);
    }
  };

  // Volver a la solicitud; si se venía de "Avisar retiro", esa ventana se
  // vuelve a abrir sola para no tener que repetir "Más acciones".
  const volver = () =>
    navigate(
      `/solicitud/${id}`,
      desdeAvisar
        ? { state: { reabrirAvisar: true, proveedorAvisar: location.state?.proveedorAvisar, avisoRetiro: location.state?.avisoRetiro } }
        : undefined
    );

  // Si se marcaron varios productos como activo, al guardar uno se abre el siguiente.
  const siguientesActivos = location.state?.siguientesActivos || [];
  const guardarYVolver = async () => {
    if (!(await guardar())) return;
    if (siguientesActivos.length > 0) {
      const [proximo, ...resto] = siguientesActivos;
      navigate(`/solicitud/${id}/activo/${proximo}`, {
        state: {
          volverAAvisar: true,
          siguientesActivos: resto,
          proveedorAvisar: location.state?.proveedorAvisar,
          avisoRetiro: location.state?.avisoRetiro,
        },
      });
      return;
    }
    volver();
  };

  if (!esCompras) return <p>No tienes permiso para ver esta pantalla.</p>;
  if (cargando) return <Cargando />;
  if (error && !solicitud) return <p style={{ color: "#a3402f" }}>{error}</p>;
  if (!solicitud || !item) return <p>No se encontró este producto en la solicitud.</p>;

  const total = unidades.length;
  const conCodigo = unidades.filter((u) => String(u.codigo_activo || "").trim()).length;
  const completas = unidades.filter(estaCompleta).length;
  const termino = filtro.trim().toLowerCase();
  const visibles = termino
    ? unidades.filter((u) =>
        [u.numero_unidad, u.codigo_activo, u.marca, u.modelo, u.numero_serie]
          .join(" ")
          .toLowerCase()
          .includes(termino)
      )
    : unidades;
  const proveedor = item.cotizacion_elegida?.proveedor_nombre;

  const estadoUnidad = (u) => {
    if (u.entregado_en) return { texto: "Entregado", color: "#1e5e3f", bg: "#dcf0e6" };
    if (u.avisado) return { texto: "Notificado", color: "#3d5a99", bg: "#e1e7f5" };
    if (estaCompleta(u)) return { texto: "Completo", color: "#2f7a54", bg: "#dcf0e6" };
    if (String(u.codigo_activo || "").trim()) return { texto: "Faltan datos", color: "#b85a0b", bg: "#fde6cf" };
    return { texto: "Sin Cod. Inventario", color: "#a3402f", bg: "#f7ded9" };
  };

  return (
    <div>
      <Link
        to={`/solicitud/${id}`}
        state={
          desdeAvisar
            ? { reabrirAvisar: true, proveedorAvisar: location.state?.proveedorAvisar, avisoRetiro: location.state?.avisoRetiro }
            : undefined
        }
        style={styles.linkVolver}
      >
        ← Regresar a la solicitud #{String(solicitud.id).padStart(3, "0")}
      </Link>

      <h2 style={{ margin: "0.6rem 0 0.8rem", color: "var(--tema-titulo, #0b3c60)" }}>🏷️ Datos de activo fijo</h2>

      <div style={styles.resumenCard}>
        <table style={styles.resumenTabla}>
          <thead>
            <tr>
              <th style={{ ...styles.resumenTh, width: "46%" }}>Artículo</th>
              <th style={styles.resumenTh}>Proveedor</th>
              <th style={{ ...styles.resumenTh, textAlign: "center", width: 120 }}>Cantidad</th>
              <th style={{ ...styles.resumenTh, textAlign: "center", width: 150 }}>Con Cod. Inventario</th>
              <th style={{ ...styles.resumenTh, textAlign: "center", width: 150 }}>Datos completos</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ ...styles.resumenTd, fontWeight: 700 }}>{item.descripcion_producto}</td>
              <td style={styles.resumenTd}>{proveedor || "—"}</td>
              <td style={{ ...styles.resumenTd, textAlign: "center" }}>
                <span style={styles.pastillaCantidad}>
                  {total} unidad{total === 1 ? "" : "es"}
                </span>
              </td>
              <td style={{ ...styles.resumenTd, textAlign: "center" }}>
                <span style={{ ...styles.chip, ...(conCodigo === total ? styles.chipOk : {}) }}>
                  {conCodigo} de {total}
                </span>
              </td>
              <td style={{ ...styles.resumenTd, textAlign: "center" }}>
                <span style={{ ...styles.chip, ...(completas === total ? styles.chipOk : {}) }}>
                  {completas} de {total}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        {desdeAvisar && (
          <div style={styles.avisoFlujo}>
            Estás llenando los datos para notificar el retiro: al guardar regresarás a la ventana de notificación automáticamente.
          </div>
        )}
      </div>

      {error && <p style={{ color: "#a3402f", fontSize: "0.85rem" }}>{error}</p>}
      {guardadoOk && <p style={{ color: "#2f7a54", fontSize: "0.85rem" }}>✅ Datos guardados.</p>}

      <div style={styles.herramientas}>
        <div style={styles.tituloHerramienta}>Aplicar a todas las unidades vacías</div>
        <div style={styles.filaLote}>
          <input
            type="text"
            placeholder="Marca"
            value={lote.marca}
            onChange={(e) => setLote({ ...lote, marca: e.target.value })}
            style={styles.input}
          />
          <input
            type="text"
            placeholder="Modelo"
            value={lote.modelo}
            onChange={(e) => setLote({ ...lote, modelo: e.target.value })}
            style={styles.input}
          />
          <input
            type="date"
            value={lote.fecha_adquisicion}
            onChange={(e) => setLote({ ...lote, fecha_adquisicion: e.target.value })}
            style={styles.input}
          />
          <select
            value={lote.edificio_id}
            onChange={(e) => setLote({ ...lote, edificio_id: e.target.value })}
            style={styles.input}
          >
            <option value="">Edificio de destino…</option>
            {edificios.map((ed) => (
              <option key={ed.id} value={ed.id}>
                {ed.nombre}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={aplicarALasVacias}
            disabled={!lote.marca && !lote.modelo && !lote.fecha_adquisicion && !lote.edificio_id}
            style={styles.botonSecundario}
          >
            ⤵ Aplicar a las vacías
          </button>
        </div>
        <Ayuda>
        <p style={styles.notaHerramienta}>
          Solo llena los campos que estén vacíos; lo que ya escribiste a mano no se cambia. El código y la serie son
          únicos por unidad.
        </p>
        </Ayuda>
      </div>

      {total > 8 && (
        <input
          type="text"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          placeholder="🔍 Buscar por número, Cod. Inventario, marca, modelo o serie..."
          style={styles.buscador}
        />
      )}

      <div className="table-scroll" style={styles.tablaWrap}>
        <table style={styles.tabla}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: 56, textAlign: "center" }}>#</th>
              <th style={styles.th}>Cod. Inventario</th>
              <th style={styles.th}>Marca</th>
              <th style={styles.th}>Modelo</th>
              <th style={styles.th}>Serie</th>
              <th style={styles.th}>Fecha de adquisición</th>
              <th style={styles.th}>Edificio de destino</th>
              <th style={{ ...styles.th, textAlign: "center" }}>Estado</th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((u, i) => {
              const est = estadoUnidad(u);
              const repetido = codigosRepetidos.has(String(u.codigo_activo || "").trim().toLowerCase());
              return (
                <tr key={u.numero_unidad} style={{ background: i % 2 === 1 ? "#fafbfc" : "#fff", borderTop: "1px solid #eef1f3" }}>
                  <td style={{ ...styles.td, textAlign: "center", fontWeight: 700, color: "#8993a1" }}>{u.numero_unidad}</td>
                  <td style={styles.td}>
                    <input
                      type="text"
                      value={u.codigo_activo}
                      onChange={(e) => actualizarUnidad(u.numero_unidad, "codigo_activo", e.target.value)}
                      style={{ ...styles.celda, ...(repetido ? styles.celdaError : {}) }}
                      title={repetido ? "Este Cod. Inventario está repetido en la tabla" : ""}
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      type="text"
                      value={u.marca}
                      onChange={(e) => actualizarUnidad(u.numero_unidad, "marca", e.target.value)}
                      style={styles.celda}
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      type="text"
                      value={u.modelo}
                      onChange={(e) => actualizarUnidad(u.numero_unidad, "modelo", e.target.value)}
                      style={styles.celda}
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      type="text"
                      value={u.numero_serie}
                      onChange={(e) => actualizarUnidad(u.numero_unidad, "numero_serie", e.target.value)}
                      style={styles.celda}
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      type="date"
                      value={u.fecha_adquisicion}
                      onChange={(e) => actualizarUnidad(u.numero_unidad, "fecha_adquisicion", e.target.value)}
                      style={styles.celda}
                    />
                  </td>
                  <td style={styles.td}>
                    <select
                      value={u.edificio_id}
                      onChange={(e) => actualizarUnidad(u.numero_unidad, "edificio_id", e.target.value)}
                      style={styles.celda}
                    >
                      <option value="">Elegir…</option>
                      {edificios.map((ed) => (
                        <option key={ed.id} value={ed.id}>
                          {ed.nombre}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ ...styles.td, textAlign: "center" }}>
                    <span
                      style={{ ...styles.estado, color: est.color, background: est.bg }}
                      title={u.entregado_a ? `Entregado a ${u.entregado_a}` : ""}
                    >
                      {est.texto}
                    </span>
                  </td>
                </tr>
              );
            })}
            {visibles.length === 0 && (
              <tr>
                <td colSpan={8} style={{ ...styles.td, textAlign: "center", color: "#8993a1" }}>
                  Ninguna unidad coincide con la búsqueda.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {codigosRepetidos.size > 0 && (
        <p style={{ color: "#a3402f", fontSize: "0.82rem", margin: "0.6rem 0 0" }}>
          ⚠️ Hay códigos de activo repetidos (marcados en rojo). Cada unidad debe tener su propio código.
        </p>
      )}

      <div style={styles.barraAcciones}>
        <span style={{ fontSize: "0.82rem", color: sinGuardar ? "#b85a0b" : "#8993a1", fontWeight: sinGuardar ? 700 : 400 }}>
          {sinGuardar ? "● Hay cambios sin guardar" : `${completas} de ${total} unidades con todos sus datos`}
        </span>
        <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
          <button type="button" onClick={volver} style={styles.botonSecundario}>
            Regresar sin guardar
          </button>
          <button type="button" onClick={guardar} disabled={guardando} style={styles.botonSecundarioFuerte}>
            {guardando ? "Guardando..." : "💾 Guardar"}
          </button>
          <button type="button" onClick={guardarYVolver} disabled={guardando} style={styles.botonGuardar}>
            {siguientesActivos.length > 0
              ? `💾 Guardar y seguir con el siguiente (${siguientesActivos.length} más)`
              : desdeAvisar
              ? "💾 Guardar y notificar retiro →"
              : "💾 Guardar y regresar"}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  linkVolver: { display: "inline-block", color: "var(--tema-titulo, #0b3c60)", fontWeight: 600, fontSize: "0.88rem", textDecoration: "none" },
  resumenCard: {
    background: "#fff",
    border: "1px solid #e2e6ea",
    borderRadius: 12,
    overflow: "hidden",
    marginBottom: "0.9rem",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
  },
  resumenTabla: { width: "100%", borderCollapse: "collapse", color: "var(--tema-titulo, #0b3c60)" },
  resumenTh: {
    padding: "0.55rem 1rem",
    background: "#eef1f3",
    fontSize: "0.72rem",
    fontWeight: 700,
    textAlign: "left",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    color: "#5b6472",
    whiteSpace: "nowrap",
  },
  resumenTd: { padding: "0.8rem 1rem", fontSize: "0.92rem", verticalAlign: "middle" },
  pastillaCantidad: { display: "inline-block", background: "#d7e8f7", color: "#1d5fa8", borderRadius: 999, padding: "0.15rem 0.75rem", fontSize: "0.82rem", fontWeight: 700, whiteSpace: "nowrap" },
  chip: { display: "inline-block", background: "#f3ecd0", color: "#9c7a1a", borderRadius: 999, padding: "0.2rem 0.75rem", fontSize: "0.8rem", fontWeight: 700, whiteSpace: "nowrap" },
  chipOk: { background: "#dcf0e6", color: "#2f7a54" },
  avisoFlujo: { background: "var(--tema-suave, #eef4fb)", color: "var(--tema-primario, #2f6690)", padding: "0.6rem 1rem", fontSize: "0.84rem", borderTop: "1px solid #e2e6ea" },
  herramientas: { background: "#fff", border: "1px solid #e2e6ea", borderRadius: 12, padding: "0.8rem 1rem", marginBottom: "0.9rem" },
  tituloHerramienta: { fontSize: "0.74rem", fontWeight: 700, color: "var(--tema-titulo, #0b3c60)", textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: "0.5rem" },
  filaLote: { display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" },
  notaHerramienta: { margin: "0.5rem 0 0", fontSize: "0.76rem", color: "#8993a1" },
  input: { border: "1px solid #d3d9df", borderRadius: 8, padding: "0.45rem 0.6rem", fontSize: "0.86rem", fontFamily: "inherit", minWidth: 130 },
  buscador: {
    width: "100%",
    boxSizing: "border-box",
    padding: "0.5rem 0.75rem",
    border: "1px solid #d3d9df",
    borderRadius: 8,
    fontFamily: "inherit",
    fontSize: "0.86rem",
    marginBottom: "0.7rem",
  },
  tablaWrap: { border: "1px solid #e2e6ea", borderRadius: 12, background: "#fff", maxHeight: "62vh", overflow: "auto", boxShadow: "0 1px 3px rgba(27,42,74,0.06)" },
  tabla: { width: "100%", minWidth: 980, borderCollapse: "collapse", fontSize: "0.86rem", color: "var(--tema-titulo, #0b3c60)" },
  th: {
    padding: "0.6rem 0.6rem",
    background: "#eef1f3",
    fontSize: "0.72rem",
    fontWeight: 700,
    textAlign: "left",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 1,
    borderBottom: "1px solid #d3d9df",
  },
  td: { padding: "0.35rem 0.5rem", verticalAlign: "middle" },
  celda: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #e2e6ea",
    borderRadius: 6,
    padding: "0.38rem 0.5rem",
    fontSize: "0.85rem",
    fontFamily: "inherit",
    color: "var(--tema-titulo, #0b3c60)",
    background: "#fff",
  },
  celdaError: { borderColor: "#a3402f", background: "#fdf1ef" },
  estado: { display: "inline-block", borderRadius: 999, padding: "0.15rem 0.65rem", fontSize: "0.74rem", fontWeight: 700, whiteSpace: "nowrap" },
  barraAcciones: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "1rem",
    flexWrap: "wrap",
    marginTop: "1rem",
    padding: "0.8rem 1rem",
    background: "#fff",
    border: "1px solid #e2e6ea",
    borderRadius: 12,
  },
  botonGuardar: { background: "#2f7a54", color: "#fff", border: "none", borderRadius: 8, padding: "0.6rem 1.2rem", fontSize: "0.88rem", fontWeight: 700, cursor: "pointer" },
  botonSecundarioFuerte: { background: "#fff", color: "#2f7a54", border: "1px solid #2f7a54", borderRadius: 8, padding: "0.6rem 1.1rem", fontSize: "0.88rem", fontWeight: 700, cursor: "pointer" },
  botonSecundario: { background: "#fff", color: "var(--tema-titulo, #0b3c60)", border: "1px solid #d3d9df", borderRadius: 8, padding: "0.55rem 1rem", fontSize: "0.86rem", fontWeight: 600, cursor: "pointer" },
};

export default DetalleActivoItem;
